import { Typography } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";

const { Text } = Typography;

// The brand red already carried by the icons this sits beside in the profile
// dropdown, rather than antd's default link blue, which reads as foreign
// against them.
const BRAND_RED = "rgb(211, 0, 26)";

/**
 * A value someone will want to take away with them — an email, a code —
 * rendered with a copy button and left selectable.
 *
 * Selecting it by dragging is the obvious thing to try and the thing most
 * likely to fail: inside a dropdown or a popover the press that starts the
 * selection is also a click, and the container usually closes on it. The copy
 * button is therefore the primary affordance, not a convenience on top of
 * one.
 *
 * Clicks are contained here. Call sites sit inside menu items and clickable
 * rows, and copying must not also navigate.
 *
 * Distinct from CopyableId, which is for short identifiers: that one is
 * nowrap with tabular figures, which would truncate an email.
 */
const CopyableText = ({
  value, empty = "—", label = "value", style = {}, muted = false,
  iconColor = BRAND_RED,
}) => {
  const raw = value === null || value === undefined ? "" : String(value).trim();

  if (!raw) {
    return (
      <div style={{ flex: 1, minWidth: 0, color: "#6c757d", ...style }}>
        {empty}
      </div>
    );
  }

  return (
    <div
      style={{ flex: 1, minWidth: 0 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Text
        style={{
          color: muted ? "#6c757d" : "#212529",
          fontWeight: 500,
          // Long addresses wrap rather than overflow the menu, and the text
          // stays selectable for anyone who still prefers to drag over it.
          wordBreak: "break-word",
          userSelect: "text",
          ...style,
        }}
        copyable={{
          text: raw,
          tooltips: [`Copy ${label}`, `${label} copied`],
          // Both states are given explicitly: pass only one and antd uses it
          // for the copied state too, so the tick would go red as well and
          // success would look like the button had not fired.
          icon: [
            <CopyOutlined key="copy" style={{ color: iconColor }} />,
            <CheckOutlined key="copied" style={{ color: "#52c41a" }} />,
          ],
        }}
      >
        {raw}
      </Text>
    </div>
  );
};

export default CopyableText;
